import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, Flex, Stack, createStyles, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconLock } from '@tabler/icons-react';

import dt from '~/domains/contest/domains/manage/domains/common/testing.const';
import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';
import { Drawer } from '~/domains/design-system/components/Drawer/Drawer';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';

import type { StaticFieldProps } from '../../types/common';
import { getFilteredSelectOptions } from '../utils/getFilteredSelectOptions';

import type { SelectModalField } from './types';

const useStyles = createStyles((theme) => ({
  card: {
    minHeight: 48,
    height: 'auto',
    width: '100%',
    paddingLeft: theme.other.spacing._12,
    paddingRight: theme.other.spacing._12,
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._8,
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.gray[3]}`,
    textAlign: 'left',
  },
  wrapper: {
    cursor: 'pointer',
    position: 'relative',
  },
  input: {
    caretColor: 'transparent',
    cursor: 'pointer',
  },
  labelWrapper: {
    width: '100%',
  },
  error: {
    borderColor: theme.colors.red[6],
  },
  tooltip: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    cursor: 'not-allowed',
  },
  tooltipLabel: {
    color: theme.colors.gray2[9],
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._8,
    paddingLeft: theme.other.spacing._16,
    paddingRight: theme.other.spacing._16,
    background: theme.colors.gray2[0],
    border: `1px solid ${theme.colors.gray2[2]}`,
    borderRadius: theme.radius.sm,
  },
  indicator: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.colors.blue[4],
  },
}));

function SelectModalTitle({ label }: { label?: string }) {
  return label ? (
    <Title ta="center" variant="headline-large">
      {label}
    </Title>
  ) : null;
}

export function SelectModalFieldElement({
  field,
  value,
  onValueChange,
  errors,
  sectionKey,
  isTouched,
  allValues,
  allErrors,
}: StaticFieldProps<SelectModalField>) {
  const [isOpen, setIsOpen] = useState(false);
  const [hover, setHover] = useState(false);

  const isError = isTouched && errors;
  const { cx, classes } = useStyles();
  const theme = useMantineTheme();
  const options = getFilteredSelectOptions({
    options: field.options,
    sectionKey,
    allValues,
    allErrors,
  });

  const selectedOption = useMemo(() => {
    const option = options.find((o) => o.value === value);
    if (option?.label) return option?.label;
    if (typeof value === 'string' || typeof value === 'number') {
      return value.toString();
    }
    if (field?.defaultValue) return field.defaultValue.toString();
    return 'None';
  }, [options, value, field?.defaultValue]);

  const handleOnChange = useCallback(
    (e) => {
      const { target } = e;
      const targetOption = options.find((option) => `${option.value}` === `${target.value}`);
      if (!targetOption) return;

      onValueChange(targetOption.value);
      setIsOpen(false);
    },
    [onValueChange, options]
  );

  useEffect(() => {
    if (!value) return;
    if (options.find((option) => option.value === value)) return;

    onValueChange(null);
  }, [onValueChange, options, value]);

  if (options.length === 0) {
    return null;
  }

  return (
    <div
      className={classes.wrapper}
      onBlur={() => setHover(false)}
      onFocus={() => setHover(!hover)}
      onClick={() => setHover(!hover)}
      onKeyDown={() => setHover(!hover)}
      onMouseLeave={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      role="button"
      tabIndex={0}
    >
      <Button
        variant="text"
        data-test-id={dt.selectOption}
        disabled={field?.disabled}
        onClick={() => setIsOpen(true)}
        className={cx(classes.card, { [classes.error]: isTouched && errors })}
      >
        <Flex align="center" justify="space-between">
          <Stack spacing={0}>
            <Flex align="center" gap={theme.other.spacing._4}>
              {field.indicator && <Box className={classes.indicator} />}
              <Text variant="body-small" c={theme.colors.gray[6]}>
                {field.label}
              </Text>
            </Flex>
            <Text variant="title-medium" c={isError ? theme.colors.red[6] : 'inherit'}>
              {isError ? errors.join(', ') : selectedOption}
            </Text>
          </Stack>

          {field?.locked ? (
            <IconLock size={24} stroke={theme.colors.gray[4]} strokeWidth={1.5} />
          ) : (
            <IconChevronDown size={18} stroke={theme.colors.gray[4]} />
          )}
        </Flex>
        {field?.locked && hover ? (
          <div className={classes.tooltip}>
            <Text className={classes.tooltipLabel} variant="body-medium">
              {field?.locked}
            </Text>
          </div>
        ) : null}
      </Button>
      <Drawer
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onToggle={setIsOpen}
        title={<SelectModalTitle label={field?.label} />}
        data-test-id={dt.drawer.container}
      >
        <Stack>
          {field?.description ? (
            <Text c="gray2.3" ta="center" variant="body-large">
              {field.description}
            </Text>
          ) : null}
          {options?.map((option, i) => {
            const isChecked = Array.isArray(value)
              ? value.includes(option.value as string | number)
              : value === option.value;

            return (
              <Tooltip
                isRendered={!!option.params?.tooltip}
                withinPortal
                position="top-start"
                key={`${option.value}`}
                label={option.params?.tooltip}
              >
                <span>
                  <Checkbox
                    pb={theme.other.spacing._8}
                    w="100%"
                    radius="lg"
                    classNames={classes}
                    data-test-id={dt.checkOption}
                    checked={isChecked}
                    wrapperProps={{ 'data-test-id': dt.field(sectionKey, [field.key]) }}
                    disabled={option.isDisabled}
                    sx={{ borderBottom: `1px solid ${theme.colors.gray2[0]}` }}
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    onChange={() => handleOnChange({ target: { value: option.value } })}
                    label={<Text variant="headline-medium">{option.label}</Text>}
                  />
                </span>
              </Tooltip>
            );
          })}
        </Stack>
      </Drawer>
    </div>
  );
}
